import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../Leads.css";
import { ArrowBigRightDashIcon, Search, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import ConfirmModal from "../../../components/ConfirmModal";
import ReasonsConfirmModal from "../../../components/ReasonsConfirmModal";
import { NOT_FOUND_M as COLUMN_MAP } from "../../../utils/column_maps";
import { getAlignClass, mapLeadDataNotFound } from "../../../utils/common";
import LoadingSpinner from "../../../components/LoadingSpinner";
import axiosInstance from "../../../api/axiosInstance";

const NotFoundM = () => {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const leadsPerPage = 25;
  const [pagination, setPagination] = useState({
    total: 0,
    showing: 0,
    page: 1,
    totalPages: 1,
  });
  const currentPageFromParams = Number(searchParams.get("page")) || 1;
  const [currentPage, setCurrentPage] = useState(currentPageFromParams);

  const totalPages = pagination.totalPages;

  const fetchLeads = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const res = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/ispeed_properties/leads-m?found=false&lead_status=0&page=${currentPage}&limit=${leadsPerPage}`);
      if (res.status !== 200) {
        throw new Error("❌ Failed to fetch leads");
      }
      const leads = await res.data;
      setPagination(
        leads.pagination || {
          total: 0,
          showing: 0,
          page: 1,
          totalPages: 1,
        },
      );
      const cleaned = leads.data.map((row) => {
        const obj = { ...row };
        COLUMN_MAP.forEach(({ key }) => {
          if (!(key in obj)) {
            obj[key] = "";
          }
        });
        return obj;
      });

      // Update leads while preserving marketData for existing zip codes
      setLeads((prevLeads) => {
        // Build a map of zip code → marketData from previous leads
        const marketDataMap = {};
        prevLeads.forEach((lead) => {
          if (lead.zipcode && lead.marketData) {
            marketDataMap[lead.zipcode] = lead.marketData;
          }
        });

        return cleaned.map((lead) => ({
          ...lead,
          marketData: lead.zipcode && marketDataMap[lead.zipcode] ? marketDataMap[lead.zipcode] : undefined,
          marketDataLoading: false, // reset loading state; will be set to true later if we fetch fresh data
        }));
      });

      // Update filteredData similarly (usually reset to the full list after fetch)
      setFilteredData((prevFiltered) => {
        const marketDataMap = {};
        prevFiltered.forEach((lead) => {
          if (lead.zipcode && lead.marketData) {
            marketDataMap[lead.zipcode] = lead.marketData;
          }
        });

        return cleaned.map((lead) => ({
          ...lead,
          marketData: lead.zipcode && marketDataMap[lead.zipcode] ? marketDataMap[lead.zipcode] : undefined,
          marketDataLoading: false,
        }));
      });

      // Fetch fresh market data only when showLoader is true (initial load or manual refresh)
      if (showLoader && cleaned.length > 0) {
        await fetchZipMarketDataBatch(cleaned);
      }
    } catch (err) {
      console.error("Error fetching leads:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchZipMarketDataBatch = async (leads) => {
    const zipList = [...new Set(leads.map((l) => l.zipcode).filter(Boolean))];
    if (zipList.length === 0) return;

    setLeads((prev) => prev.map((lead) => (zipList.includes(lead.zipcode) ? { ...lead, marketDataLoading: true } : lead)));
    setFilteredData((prev) => prev.map((lead) => (zipList.includes(lead.zipcode) ? { ...lead, marketDataLoading: true } : lead)));

    try {
      const response = await axiosInstance.post(`/api/webhooks/market-data-zips`, { zips: zipList });

      if (response.data?.success && Array.isArray(response.data.data)) {
        const marketDataMap = Object.fromEntries(response.data.data.map((item) => [item.zip, item.normalizedMarket || {}]));

        setLeads((prev) =>
          prev.map((lead) => ({
            ...lead,
            marketData: marketDataMap[lead.zipcode] || lead.marketData,
            marketDataLoading: false,
          })),
        );
        setFilteredData((prev) =>
          prev.map((lead) => ({
            ...lead,
            marketData: marketDataMap[lead.zipcode] || lead.marketData,
            marketDataLoading: false,
          })),
        );
      } else {
        clearMarketDataLoading(zipList);
      }
    } catch (error) {
      console.error("Failed to fetch ZIP market data", error);
      clearMarketDataLoading(zipList);
    }
  };

  const clearMarketDataLoading = (zipList) => {
    setLeads((prev) => prev.map((lead) => (zipList.includes(lead.zipcode) ? { ...lead, marketDataLoading: false } : lead)));
    setFilteredData((prev) => prev.map((lead) => (zipList.includes(lead.zipcode) ? { ...lead, marketDataLoading: false } : lead)));
  };

  const handleDeleteLead = async (identifier, status) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}api/ispeed_properties/lead-m/${identifier}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ lead_status: status }),
      });

      if (!res.ok) throw new Error("❌ Failed to Delete lead");

      toast.success("Lead Deleted successfully.");

      fetchLeads();
    } catch (err) {
      console.error("Error updating lead status:", err);
      toast.error(err.message || "Error Deleting Lead.");
    }
  };

  const handleOpenMoveModal = (row) => {
    setSelectedRow(row);
    setMoveModalOpen(true);
  };

  const handleOpenDeleteModal = (row) => {
    setSelectedRow(row);
    setDeleteModalOpen(true);
  };

  const changePage = (page) => {
    if (page < 1 || page > totalPages) return;

    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("page", page);
    setSearchParams(newParams);
    setCurrentPage(page);
  };

  useEffect(() => {
    const page = Number(searchParams.get("page")) || 1;
    setCurrentPage(page);
  }, [searchParams]);

  useEffect(() => {
    fetchLeads(true);

    const interval = setInterval(() => {
      fetchLeads(false);
    }, 60000);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [currentPage]);

  useEffect(() => {
    if (!search) {
      setFilteredData(leads);
      return;
    }

    const searchLower = search.toLowerCase();
    const filtered = leads.filter((row) => Object.values(row).some((value) => value && value.toString().toLowerCase().includes(searchLower)));

    setFilteredData(filtered);

    setCurrentPage(1);
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("page", 1);
    setSearchParams(newParams);
    // eslint-disable-next-line
  }, [search]);

  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 3;

    if (totalPages > 0) {
      buttons.push(
        <button key={1} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === 1 ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => changePage(1)}>
          1
        </button>,
      );
    }

    let startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(2, endPage - maxVisiblePages + 1);
    }

    if (startPage > 2) {
      buttons.push(
        <span key="ellipsis-start" className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">
          ...
        </span>,
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button key={i} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-pointer ${currentPage === i ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => changePage(i)}>
          {i}
        </button>,
      );
    }

    if (endPage < totalPages - 1 && totalPages > 1) {
      buttons.push(
        <span key="ellipsis-end" className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">
          ...
        </span>,
      );
    }

    if (totalPages > 1) {
      buttons.push(
        <button key={totalPages} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === totalPages ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => changePage(totalPages)}>
          {totalPages}
        </button>,
      );
    }

    return buttons;
  };

  const totalTableWidth = COLUMN_MAP.reduce((sum, col) => sum + col.width, 0) + 80;
  return (
    <>
      <div className="input_fields_container">
        <div className="">
          <input type="text" placeholder="Search leads..." value={search} onChange={(e) => setSearch(e.target.value)} className="!mb-4 !p-2 !border !border-gray-300 !rounded-lg !w-full focus:!outline-none focus:!ring-2 focus:!ring-blue-500" />
        </div>
      </div>

      <div className="">
        <div className="!overflow-auto !w-full">
          <table className="!border !border-gray-300 !bg-white !rounded-lg !shadow !table-auto" style={{ width: `${totalTableWidth}px` }}>
            <thead className="!bg-gray-100 sticky top-0 z-[1]">
              <tr>
                <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>
                <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>
                <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>
                {COLUMN_MAP.map((col) => (
                  <th
                    key={col.key}
                    className={`!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap ${getAlignClass(col.headerAlign)}`}
                    style={{
                      minWidth: `${col.width}px`,
                      width: `${col.width}px`,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading ? (
                <>
                  {filteredData.length > 0 ? (
                    filteredData.map((lead, idx) => {
                      const row = mapLeadDataNotFound(lead);
                      return (
                        <tr key={idx} className="hover:!bg-gray-50 even:!bg-gray-50">
                          <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                            <a target="_blank" rel="noreferrer" href={`/property-search?city=${encodeURIComponent(row["city"])}&state=${encodeURIComponent(row["state"])}&zipcode=${encodeURIComponent(row["zipcode"])}&county=${encodeURIComponent(row["county"])}&address=${encodeURIComponent(row["address"])}&phone=${encodeURIComponent(row["phone"])}&email=${encodeURIComponent(row["email"])}&ownername=${encodeURIComponent(row["name"])}&lead-m=${encodeURIComponent(row["identifier"])}`} className="!text-blue-600 hover:!text-blue-800 flex items-center justify-center" title="Search Property">
                              <Search size={18} />
                            </a>
                          </td>

                          <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                            <button onClick={() => handleOpenMoveModal(row)} className="!text-blue-600 hover:!text-blue-800 !flex !items-center !justify-center !px-1 !py-2 cursor-pointer" title="Move Lead">
                              <ArrowBigRightDashIcon size={18} />
                            </button>
                          </td>
                          <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                            <button onClick={() => handleOpenDeleteModal(row)} className="!text-red-600 hover:!text-red-800 !flex !items-center !justify-center !px-1 !py-2 cursor-pointer" title="Delete Lead">
                              <Trash2 size={18} />
                            </button>
                          </td>

                          {COLUMN_MAP.map((col) => (
                            <td
                              key={col.key}
                              className={`!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap ${getAlignClass(col.cellAlign)}`}
                              style={{
                                maxWidth: `${col.width}px`,
                                width: `${col.width}px`,
                              }}
                              title={row[col.key]}
                            >
                              {row[col.key]}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={COLUMN_MAP.length + 3} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                        <div className="w-full max-w-screen text-center">No leads found.</div>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <>
                  <tr className="hover:!bg-gray-50 even:!bg-gray-50">
                    <td colSpan={COLUMN_MAP.length + 3} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                      <div className="w-full max-w-screen text-center">
                        <LoadingSpinner size={30} textSize={20} />
                      </div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {filteredData.length > 0 && (
          <div className="flex flex-col items-center !mb-10 mt-4">
            <span className="text-sm text-gray-700">
              Showing
              <span className="font-semibold text-gray-900">{pagination.total === 0 ? 0 : (currentPage - 1) * leadsPerPage + 1}</span>
              to
              <span className="font-semibold text-gray-900">{(currentPage - 1) * leadsPerPage + filteredData.length}</span>
              of
              <span className="font-semibold text-gray-900">{pagination.total}</span>
              Entries
            </span>

            <div className="inline-flex !mt-2 xs:mt-0">
              <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-s cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => changePage(currentPage - 1)} disabled={currentPage === 1}>
                Prev
              </button>

              {getPaginationButtons()}

              <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-e cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => changePage(currentPage + 1)} disabled={currentPage === totalPages}>
                Next
              </button>
            </div>
          </div>
        )}
        <ReasonsConfirmModal isOpen={moveModalOpen} setMoveModalOpen={setMoveModalOpen} onCancel={() => setMoveModalOpen(false)} selectedRow={selectedRow} identifier={selectedRow?.identifier} source="m" fetchLeads={fetchLeads} />

        {/* Delete Confirm Modal */}
        <ConfirmModal
          title="Confirm Delete"
          message={`Are you sure you want to delete this lead?`}
          isOpen={deleteModalOpen}
          onConfirmTitle="Delete"
          onConfirm={() => {
            if (selectedRow) {
              handleDeleteLead(selectedRow.identifier, 1);
            }
            setDeleteModalOpen(false);
          }}
          onCancelTitle="Cancel"
          onCancel={() => setDeleteModalOpen(false)}
        />
      </div>
    </>
  );
};

export default NotFoundM;
